import { ApiService } from './api';
import type { 
  AudioTranscriptionRequest, 
  AudioTranscriptionResponse, 
  VoiceMessageConfig, 
  MicrophonePermissionState,
  VoiceError,
  VoiceErrorType,
  AudioLevelData
} from '../types/voice';
import { DEFAULT_VOICE_CONFIG } from '../types/voice';

export class VoiceService {
  private mediaRecorder: MediaRecorder | null = null;
  private audioStream: MediaStream | null = null;
  private audioContext: AudioContext | null = null;
  private analyser: AnalyserNode | null = null;
  private dataArray: Uint8Array<ArrayBuffer> | null = null;
  private animationFrame: number | null = null;
  private config: VoiceMessageConfig;
  private onAudioLevel?: (level: number) => void;

  constructor(config: Partial<VoiceMessageConfig> = {}) {
    this.config = { ...DEFAULT_VOICE_CONFIG, ...config };
  }

  /**
   * Check if voice recording is supported in the current browser
   */
  static isSupported(): boolean {
    return !!(
      navigator.mediaDevices &&
      navigator.mediaDevices.getUserMedia &&
      window.MediaRecorder &&
      window.AudioContext
    );
  }

  /**
   * Check microphone permission status
   */
  async checkMicrophonePermission(): Promise<MicrophonePermissionState> {
    try {
      if (!navigator.permissions) {
        return 'unknown';
      }

      const permission = await navigator.permissions.query({ name: 'microphone' as PermissionName });
      return permission.state as MicrophonePermissionState;
    } catch (error) {
      console.warn('Could not check microphone permission:', error);
      return 'unknown';
    }
  }

  /**
   * Request microphone access and initialize audio stream
   */
  async requestMicrophoneAccess(): Promise<MediaStream> {
    try {
      console.log('VoiceService: Requesting microphone access...');
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          sampleRate: this.config.sampleRate,
          channelCount: this.config.channels,
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        }
      });

      this.audioStream = stream;
      console.log('VoiceService: Audio stream initialized successfully');
      await this.setupAudioAnalysis(stream);
      return stream;
    } catch (error) {
      console.error('VoiceService: Failed to request microphone access:', error);
      throw this.createVoiceError('permission_denied', 'Không thể truy cập microphone', error);
    }
  }

  /**
   * Check if audio stream is active and available
   */
  isAudioStreamActive(): boolean {
    if (!this.audioStream) {
      console.log('VoiceService: No audio stream available');
      return false;
    }

    const tracks = this.audioStream.getAudioTracks();
    const isActive = tracks.length > 0 && tracks.every(track => track.readyState === 'live');
    console.log('VoiceService: Audio stream active:', isActive, 'tracks:', tracks.length);
    return isActive;
  }

  /**
   * Setup audio analysis for real-time level monitoring
   */
  private async setupAudioAnalysis(stream: MediaStream): Promise<void> {
    try {
      this.audioContext = new AudioContext();
      const source = this.audioContext.createMediaStreamSource(stream);
      this.analyser = this.audioContext.createAnalyser();
      
      this.analyser.fftSize = 256;
      this.analyser.smoothingTimeConstant = 0.8;
      
      source.connect(this.analyser);
      // Use an ArrayBuffer-backed Uint8Array to satisfy TS typed array generics
      this.dataArray = new Uint8Array(new ArrayBuffer(this.analyser.frequencyBinCount));
    } catch (error) {
      console.warn('Could not setup audio analysis:', error);
    }
  }

  /**
   * Start monitoring audio levels
   */
  startAudioLevelMonitoring(callback: (level: number) => void): void {
    this.onAudioLevel = callback;
    this.monitorAudioLevel();
  }

  /**
   * Stop monitoring audio levels
   */
  stopAudioLevelMonitoring(): void {
    this.onAudioLevel = undefined;
    if (this.animationFrame) {
      cancelAnimationFrame(this.animationFrame);
      this.animationFrame = null;
    }
  }

  /**
   * Monitor audio level in real-time
   */
  private monitorAudioLevel(): void {
    if (!this.analyser || !this.dataArray || !this.onAudioLevel) {
      return;
    }

    this.analyser.getByteFrequencyData(this.dataArray);
    
    // Calculate average audio level
    let sum = 0;
    for (let i = 0; i < this.dataArray.length; i++) {
      sum += this.dataArray[i];
    }
    const average = sum / this.dataArray.length;
    const normalizedLevel = average / 255; // Normalize to 0-1

    this.onAudioLevel(normalizedLevel);

    this.animationFrame = requestAnimationFrame(() => this.monitorAudioLevel());
  }

  /**
   * Start recording audio
   */
  async startRecording(): Promise<void> {
    console.log('VoiceService: Starting recording...');
    
    // Ensure we have an active audio stream; try to initialize if missing/inactive
    if (!this.audioStream || !this.isAudioStreamActive()) {
      try {
        console.log('VoiceService: Audio stream missing/inactive, requesting access...');
        await this.requestMicrophoneAccess();
      } catch (err) {
        console.error('VoiceService: Unable to initialize audio stream', err);
        throw this.createVoiceError('recording_failed', 'Không thể khởi tạo audio stream', err);
      }
    }

    // Resume audio context if suspended due to autoplay policy
    try {
      if (this.audioContext && this.audioContext.state === 'suspended') {
        await this.audioContext.resume();
      }
    } catch (err) {
      console.warn('VoiceService: Failed to resume AudioContext', err);
    }

    try {
      const options: MediaRecorderOptions = {
        mimeType: this.config.audioFormat
      };

      // Fallback to supported format if preferred format is not available
      if (!MediaRecorder.isTypeSupported(this.config.audioFormat)) {
        const fallbackFormats = [
          'audio/webm;codecs=opus',
          'audio/webm',
          'audio/mp4',
          'audio/wav'
        ];

        for (const format of fallbackFormats) {
          if (MediaRecorder.isTypeSupported(format)) {
            options.mimeType = format;
            break;
          }
        }
      }

      this.mediaRecorder = new MediaRecorder(this.audioStream, options);
      
      return new Promise((resolve, reject) => {
        if (!this.mediaRecorder) {
          reject(this.createVoiceError('recording_failed', 'MediaRecorder not initialized'));
          return;
        }

        this.mediaRecorder.onstart = () => {
          resolve();
        };

        this.mediaRecorder.onerror = (event) => {
          reject(this.createVoiceError('recording_failed', 'Recording failed', event));
        };

        this.mediaRecorder.start();
      });
    } catch (error) {
      throw this.createVoiceError('recording_failed', 'Failed to start recording', error);
    }
  }

  /**
   * Stop recording and return audio blob
   */
  async stopRecording(): Promise<Blob> {
    if (!this.mediaRecorder || this.mediaRecorder.state !== 'recording') {
      throw this.createVoiceError('recording_failed', 'No active recording');
    }

    return new Promise((resolve, reject) => {
      if (!this.mediaRecorder) {
        reject(this.createVoiceError('recording_failed', 'MediaRecorder not available'));
        return;
      }

      const chunks: Blob[] = [];

      this.mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunks.push(event.data);
        }
      };

      this.mediaRecorder.onstop = () => {
        const audioBlob = new Blob(chunks, { type: this.config.audioFormat });
        resolve(audioBlob);
      };

      this.mediaRecorder.onerror = (event) => {
        reject(this.createVoiceError('recording_failed', 'Recording stop failed', event));
      };

      this.mediaRecorder.stop();
    });
  }

  /**
   * Cancel recording and cleanup
   */
  cancelRecording(): void {
    if (this.mediaRecorder && this.mediaRecorder.state === 'recording') {
      this.mediaRecorder.stop();
    }
    this.cleanup();
  }

  /**
   * Transcribe audio using backend API
   */
  async transcribeAudio(request: AudioTranscriptionRequest): Promise<AudioTranscriptionResponse> {
    try {
      return await ApiService.transcribeAudio(request);
    } catch (error) {
      console.error('Transcription error:', error);
      
      return {
        success: false,
        text: '',
        error: error instanceof Error ? error.message : 'Unknown transcription error'
      };
    }
  }

  /**
   * Cleanup resources
   */
  cleanup(): void {
    this.stopAudioLevelMonitoring();

    if (this.audioStream) {
      this.audioStream.getTracks().forEach(track => track.stop());
      this.audioStream = null;
    }

    if (this.audioContext && this.audioContext.state !== 'closed') {
      this.audioContext.close();
      this.audioContext = null;
    }

    this.analyser = null;
    this.dataArray = null;
    this.mediaRecorder = null;
  }

  /**
   * Create a standardized voice error
   */
  private createVoiceError(type: VoiceErrorType, message: string, originalError?: any): VoiceError {
    const error: VoiceError = {
      type,
      message,
      details: originalError ? String(originalError) : undefined
    };

    console.error('Voice Service Error:', error);
    return error;
  }

  /**
   * Get audio duration from blob
   */
  static async getAudioDuration(audioBlob: Blob): Promise<number> {
    return new Promise((resolve, reject) => {
      const audio = new Audio();
      const url = URL.createObjectURL(audioBlob);
      
      audio.onloadedmetadata = () => {
        URL.revokeObjectURL(url);
        resolve(audio.duration);
      };
      
      audio.onerror = () => {
        URL.revokeObjectURL(url);
        reject(new Error('Could not load audio'));
      };
      
      audio.src = url;
    });
  }

  /**
   * Validate audio blob
   */
  static validateAudioBlob(audioBlob: Blob, maxDuration: number): { isValid: boolean; error?: string } {
    if (!audioBlob || audioBlob.size === 0) {
      return { isValid: false, error: 'Audio file is empty' };
    }

    // Check file size (rough estimate: 1 minute of audio ≈ 1MB)
    const maxSizeBytes = maxDuration * 1024 * 1024; // MB per minute
    if (audioBlob.size > maxSizeBytes) {
      return { isValid: false, error: 'Audio file too large' };
    }

    return { isValid: true };
  }
}